"use client";
import { useMemo, useRef, type ReactElement } from "react";
import { TIMEZONES, type TimezoneOption } from "@/lib/timezones";
import { cn } from "@/lib/cn";

type Props = {
  value: string;
  onChange: (tz: string) => void;
  className?: string;
};

const KNOWN_ZONES = new Set(TIMEZONES.map((tz) => tz.value));

export function TimezoneSelector({ value, onChange, className }: Props): ReactElement {
  // TIMEZONES comes from Intl.supportedValuesOf, which lists only the CANONICAL
  // ids for the runtime's ICU build — every alias is missing, and which ids are
  // aliases moves between ICU versions (Asia/Calcutta and Asia/Kolkata swap
  // places, for one). A `value` that is not in the list leaves the select with
  // no matching option, so it silently displays some other zone while slots are
  // fetched in the real one. The zone can now arrive from the backend as the
  // link's pinned `defaultTimezone`, so it is not just the browser's to vouch
  // for: carry whatever we were handed as its own option.
  //
  // The injected option has to OUTLIVE the value that introduced it. Keying it
  // to the current `value` alone means the moment the lead tries another zone
  // the pinned one disappears from the list — and with it any way back to it.
  const carriedRef = useRef<string[]>([]);

  const options: TimezoneOption[] = useMemo(() => {
    const carried = carriedRef.current;
    if (value && !KNOWN_ZONES.has(value) && !carried.includes(value)) {
      // Append-only and de-duplicated, so recomputing this memo — which React
      // may do at any time — can never grow the list twice for one zone.
      carriedRef.current = [...carried, value];
    }
    if (carriedRef.current.length === 0) return TIMEZONES;
    return [
      ...carriedRef.current.map((tz) => ({ value: tz, label: tz })),
      ...TIMEZONES,
    ];
  }, [value]);

  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className={cn(
        "w-full rounded-lg border border-neutral-200 bg-white px-3 py-2 text-sm text-neutral-700 focus:border-[#029391] focus:outline-none focus:ring-1 focus:ring-[#029391]",
        className
      )}
    >
      {options.map((tz) => (
        <option key={tz.value} value={tz.value}>
          {tz.label}
        </option>
      ))}
    </select>
  );
}
